import { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles, Loader2, Trash2, GripVertical, Plus, ChevronUp, ChevronDown,
  BookOpen, Wand2, FileUp, FilePlus2, LayoutTemplate, Save, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * AIStructureBuilderDialog — Sprint 1, Step 2 + Step 36 (Reference PDF import).
 *
 * Two source modes (selectable as tabs at the top):
 *   • "AI Generate" — GPT-5.2 designs an architecture from title/specialty/RQ.
 *   • "Import from Reference PDF" — mines an existing dissertation's
 *     Table of Contents (depths 0–3) and proposes it as a starting point.
 *
 * Both modes feed the same review screen, where the user can edit titles,
 * descriptions, reorder, indent/outdent, delete or insert chapters before
 * applying. Sub-sections are visually indented by `depth`.
 */
export default function AIStructureBuilderDialog({
  open, onOpenChange,
  userId, project,
  onApplied,
}) {
  const [source, setSource] = useState('ai');   // 'ai' | 'import' | 'template'
  const [templates, setTemplates] = useState({ builtin: [], templates: [] });
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [phase, setPhase] = useState('idle');   // idle | generating | review | applying
  const [chapters, setChapters] = useState([]);
  const [methodologyNotes, setMethodologyNotes] = useState('');
  const [suggestedGuideline, setSuggestedGuideline] = useState(null);
  const [sourceMeta, setSourceMeta] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);

  // AI mode editable hints
  const [specialty, setSpecialty] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [expectedStructure, setExpectedStructure] = useState('');

  // Import mode
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  useEffect(() => {
    if (open && project) {
      setSpecialty(project.specialty || '');
      setHypothesis(project.hypothesis || '');
      setExpectedStructure(project.expected_structure || '');
    }
  }, [open, project]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      setTemplates(await thesisApi.listStructureTemplates(userId, project?.project_type));
    } catch {
      toast.error('Could not load templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, [userId, project?.project_type]);

  useEffect(() => {
    if (open && source === 'template') loadTemplates();
  }, [open, source, loadTemplates]);

  const applyTemplate = (tpl) => {
    setChapters((tpl.chapters || []).map((c, i) => ({
      subtopics: [], suggested_evidence_keywords: [], rationale: '',
      ...c, order: i,
      temp_id: c.temp_id || `tmp_${Math.random().toString(16).slice(2, 12)}`,
    })));
    setMethodologyNotes(tpl.notes || '');
    setSuggestedGuideline(null);
    setSourceMeta(null);
    setPhase('review');
    toast.success(`Loaded "${tpl.name}" — ${tpl.chapters?.length || 0} sections`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Template name', `${project?.title || 'My'} structure`);
    if (!name) return;
    setSavingTemplate(true);
    try {
      await thesisApi.saveStructureTemplate(userId, {
        name,
        project_type: project?.project_type || null,
        chapters: chapters.map((c, i) => ({
          order: i, title: c.title, description: c.description || '', depth: c.depth || 0,
          parent_temp_id: c.parent_temp_id || null, temp_id: c.temp_id,
        })),
      });
      toast.success('Saved as template — reuse it in any project');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the template');
    } finally {
      setSavingTemplate(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSource('ai');
      setPhase('idle');
      setChapters([]);
      setMethodologyNotes('');
      setSuggestedGuideline(null);
      setSourceMeta(null);
      setReplaceExisting(false);
      setImportFile(null);
      setImportProgress(0);
    }
  }, [open]);

  const handleGenerateAI = useCallback(async () => {
    setPhase('generating');
    try {
      // Sprint 6 — background-job pattern. Kick off the job…
      const queued = await thesisApi.suggestDissertationStructure(userId, project.id, {
        specialty, hypothesis, expected_structure: expectedStructure,
      });
      if (!queued.job_id) {
        // Backwards-compat: server returned the result inline (old sync API)
        setChapters(queued.chapters || []);
        setMethodologyNotes(queued.methodology_notes || '');
        setSuggestedGuideline(queued.suggested_guideline_id || null);
        setSourceMeta(null);
        setPhase('review');
        toast.success(`Proposed ${queued.chapters?.length || 0} chapters`);
        return;
      }
      // …then poll every 3 s up to ~5 minutes
      const jobId = queued.job_id;
      const started = Date.now();
      let lastProgress = 'Queued — preparing prompt…';
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          job = await thesisApi.getStructureJob(userId, jobId);
        } catch (pollErr) {
          // network blip — keep trying
          continue;
        }
        if (job.progress && job.progress !== lastProgress) {
          lastProgress = job.progress;
        }
        if (job.status === 'ready') {
          setChapters(job.chapters || []);
          setMethodologyNotes(job.methodology_notes || '');
          setSuggestedGuideline(job.suggested_guideline_id || null);
          setSourceMeta(null);
          setPhase('review');
          toast.success(`Proposed ${job.chapters?.length || 0} chapters`);
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Structure builder job failed');
        }
      }
      throw new Error('Structure builder timed out after 5 minutes. Please retry.');
    } catch (e) {
      toast.error(`Failed to suggest structure: ${e?.response?.data?.detail || e.message}`);
      setPhase('idle');
    }
  }, [userId, project, specialty, hypothesis, expectedStructure]);

  const handleImport = useCallback(async () => {
    if (!importFile) {
      toast.error('Please pick a PDF file first.');
      return;
    }
    setPhase('generating');
    setImportProgress(0);
    try {
      const d = await thesisApi.importReferenceStructure(
        userId, project.id, importFile, setImportProgress
      );
      setChapters(d.chapters || []);
      setMethodologyNotes(d.methodology_notes || '');
      setSuggestedGuideline(d.suggested_guideline_id || null);
      setSourceMeta(d.source_meta || null);
      setPhase('review');
      toast.success(`Extracted ${d.chapters?.length || 0} sections from "${d.source_meta?.title || importFile.name}"`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Import failed: ${msg}`);
      setPhase('idle');
    }
  }, [userId, project, importFile]);

  const handleApply = useCallback(async () => {
    if (!chapters.length) return;
    setPhase('applying');
    try {
      // Re-normalise orders — keep depth + parent_temp_id intact
      const normalised = chapters.map((c, i) => ({ ...c, order: i }));
      const res = await thesisApi.applyDissertationStructure(
        userId, project.id, normalised, replaceExisting
      );
      toast.success(`Applied ${res.inserted} chapters`);
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Apply failed: ${e?.response?.data?.detail || e.message}`);
      setPhase('review');
    }
  }, [chapters, userId, project, replaceExisting, onApplied, onOpenChange]);

  // ---------- Review-mode editing helpers ----------
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= chapters.length) return;
    const next = [...chapters];
    [next[idx], next[j]] = [next[j], next[idx]];
    setChapters(next.map((c, i) => ({ ...c, order: i })));
  };
  const remove = (idx) => {
    // Also drop any descendants of the removed node
    const removed = chapters[idx];
    const removedTemp = removed?.temp_id;
    const survivors = chapters.filter((c, i) => {
      if (i === idx) return false;
      // walk parent chain — if it includes removedTemp, drop
      let cur = c.parent_temp_id;
      let safety = 0;
      while (cur && safety++ < 10) {
        if (cur === removedTemp) return false;
        cur = chapters.find((x) => x.temp_id === cur)?.parent_temp_id;
      }
      return true;
    });
    setChapters(survivors.map((c, i) => ({ ...c, order: i })));
  };
  const updateField = (idx, field, value) => {
    const next = [...chapters];
    next[idx] = { ...next[idx], [field]: value };
    setChapters(next);
  };
  const insertAfter = (idx) => {
    const next = [...chapters];
    const base = chapters[idx] || { depth: 0, parent_temp_id: null };
    next.splice(idx + 1, 0, {
      order: idx + 1,
      title: 'New chapter',
      description: '',
      rationale: 'Manually inserted by author.',
      subtopics: [],
      suggested_evidence_keywords: [],
      depth: base.depth,
      parent_temp_id: base.parent_temp_id,
      temp_id: `tmp_${Math.random().toString(16).slice(2, 12)}`,
    });
    setChapters(next.map((c, i) => ({ ...c, order: i })));
  };
  const changeDepth = (idx, delta) => {
    const next = [...chapters];
    const cur = next[idx];
    const newDepth = Math.max(0, Math.min(3, (cur.depth || 0) + delta));
    // Find a plausible new parent: nearest preceding row with depth = newDepth - 1
    let newParent = null;
    if (newDepth > 0) {
      for (let i = idx - 1; i >= 0; i--) {
        if ((next[i].depth || 0) === newDepth - 1) { newParent = next[i].temp_id; break; }
      }
    }
    next[idx] = { ...cur, depth: newDepth, parent_temp_id: newParent };
    setChapters(next);
  };

  const busy = phase === 'generating' || phase === 'applying';

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ai-structure-builder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Wand2 className="h-5 w-5 text-blue-600" />
            AI Structure Builder
          </DialogTitle>
          <DialogDescription>
            Propose the canonical section architecture for this document type — generated by AI or imported from a reference PDF.
          </DialogDescription>
        </DialogHeader>

        {/* Source-mode tabs (idle phase only) */}
        {phase === 'idle' && (
          <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 -mb-px">
            <button
              type="button"
              data-testid="structure-source-ai"
              onClick={() => setSource('ai')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                source === 'ai'
                  ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Sparkles className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
              AI Generate
            </button>
            <button
              type="button"
              data-testid="structure-source-import"
              onClick={() => setSource('import')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                source === 'import'
                  ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <FileUp className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
              Import from Reference PDF
            </button>
            <button
              type="button"
              data-testid="structure-source-template"
              onClick={() => setSource('template')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                source === 'template'
                  ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <LayoutTemplate className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
              Templates
            </button>
          </div>
        )}

        {/* Phase 1C — Templates: instant, no LLM */}
        {phase === 'idle' && source === 'template' && (
          <div className="space-y-4 pt-4" data-testid="structure-templates-pane">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:bg-slate-900 dark:border-slate-700">
              <Zap className="h-4 w-4 inline-block mr-1.5 -mt-0.5 text-amber-500" />
              Apply a ready structure in one click — no AI, no waiting. Your own saved templates
              appear first, followed by the canonical skeleton of each document type.
            </div>
            {templatesLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            ) : (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {templates.templates.map((tpl) => (
                  <div key={tpl.id} data-testid={`structure-template-${tpl.id}`}
                    className="rounded-md border border-blue-200 dark:border-blue-900 p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{tpl.name}</p>
                      <p className="text-xs text-slate-500">
                        Your template · {tpl.chapters?.length || 0} sections
                        {tpl.project_type ? ` · ${tpl.project_type.replace(/_/g, ' ')}` : ''}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => applyTemplate(tpl)}
                      data-testid={`structure-template-use-${tpl.id}`}>Use</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                      title="Delete template"
                      onClick={async () => {
                        await thesisApi.deleteStructureTemplate(userId, tpl.id);
                        toast.success('Template deleted');
                        loadTemplates();
                      }}
                      data-testid={`structure-template-delete-${tpl.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {templates.builtin.map((tpl) => (
                  <div key={tpl.id} data-testid={`structure-template-${tpl.id}`}
                    className="rounded-md border border-slate-200 dark:border-slate-700 p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{tpl.name}</p>
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {tpl.chapters.map((c) => c.title).join(' · ')}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => applyTemplate(tpl)}
                      data-testid={`structure-template-use-${tpl.id}`}>Use</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => onOpenChange(false)}
                data-testid="structure-builder-cancel-template">Cancel</Button>
            </div>
          </div>
        )}

        {/* Phase 1A — AI mode */}
        {phase === 'idle' && source === 'ai' && (
          <div className="space-y-4 pt-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-200">
              <strong>{(project?.project_type || 'manuscript').replace(/_/g, ' ')}:</strong> {project?.title || '(untitled)'}<br/>
              <strong>Research question:</strong> {project?.research_question || '—'}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">Specialty (refine)</label>
                <Input data-testid="structure-builder-specialty"
                  placeholder="e.g., general surgery, cardiology, gastroenterology"
                  value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">Hypothesis (optional)</label>
                <Textarea data-testid="structure-builder-hypothesis" rows={2}
                  placeholder="e.g., Early surgical intervention reduces 30-day mortality in severe acute pancreatitis vs. conservative management."
                  value={hypothesis} onChange={(e) => setHypothesis(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">Expected structure (optional — guides the AI)</label>
                <Textarea data-testid="structure-builder-expected-structure" rows={3}
                  placeholder="e.g., Intro · Epidemiology · Pathophysiology · Diagnostics · Conservative therapy · Surgical management · Outcomes · Discussion · Conclusions"
                  value={expectedStructure} onChange={(e) => setExpectedStructure(e.target.value)} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="structure-builder-cancel">Cancel</Button>
              <Button onClick={handleGenerateAI} data-testid="structure-builder-generate-btn">
                <Sparkles className="h-4 w-4 mr-2" />Propose Structure
              </Button>
            </div>
          </div>
        )}

        {/* Phase 1B — Import mode */}
        {phase === 'idle' && source === 'import' && (
          <div className="space-y-4 pt-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-200">
              Upload a published dissertation, thesis or long article (PDF) and we'll mine its Table of Contents — the hierarchy (up to 3 depth levels) becomes your starting structure. You can edit, indent, and reorder before applying.
            </div>

            <div className="rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 px-6 py-8 text-center hover:border-blue-400 transition-colors">
              <FilePlus2 className="h-10 w-10 mx-auto text-slate-400 mb-2" />
              {importFile ? (
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{importFile.name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(importFile.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                  <Button variant="ghost" size="sm" className="mt-2 text-xs"
                    onClick={() => { setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    data-testid="structure-import-clear">
                    Pick a different file
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">Drop a PDF here or browse</p>
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="structure-import-browse">
                    Browse files
                  </Button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="structure-import-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && !f.name.toLowerCase().endsWith('.pdf')) {
                    toast.error('Only PDF files are supported.');
                    return;
                  }
                  setImportFile(f || null);
                }}
              />
              <p className="text-xs text-slate-400 mt-3">
                Max 30 MB. We parse the printed Table of Contents — most academic theses work out of the box.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="structure-builder-cancel-import">Cancel</Button>
              <Button onClick={handleImport} disabled={!importFile} data-testid="structure-import-submit">
                <FileUp className="h-4 w-4 mr-2" />Extract Structure
              </Button>
            </div>
          </div>
        )}

        {/* Phase 2 — Loading */}
        {phase === 'generating' && (
          <div className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-3">
              {source === 'import' ? 'Reading the reference PDF and mining its Table of Contents…' : 'Designing the section architecture for your document type…'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {source === 'import'
                ? (importProgress > 0 && importProgress < 100 ? `Uploaded ${importProgress}% · then ~2 s parsing` : 'Usually 2–5 seconds.')
                : 'This usually takes 30–60 seconds.'}
            </p>
          </div>
        )}

        {/* Phase 3 — Review + Apply */}
        {(phase === 'review' || phase === 'applying') && (
          <div className="space-y-3">
            {sourceMeta && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-200">
                <p className="font-semibold mb-1">Source dissertation</p>
                <p className="leading-relaxed">
                  <strong>{sourceMeta.title || '(untitled)'}</strong>
                  {sourceMeta.author ? ` — ${sourceMeta.author}` : ''} · {sourceMeta.pages} pages · {sourceMeta.nodes_found} sections (depth up to {sourceMeta.max_depth_seen})
                </p>
              </div>
            )}

            {methodologyNotes && !sourceMeta && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-200">
                <p className="font-semibold mb-1">Methodology notes</p>
                <p className="leading-relaxed">{methodologyNotes}</p>
                {suggestedGuideline && (
                  <p className="mt-2 text-xs">Suggested EQUATOR guideline: <code>{suggestedGuideline}</code></p>
                )}
              </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-300">
              {chapters.length} chapters proposed · use arrows to reorder or indent/outdent · edit titles/descriptions inline
            </p>

            <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
              {chapters.map((ch, idx) => (
                <div
                  key={ch.temp_id || idx}
                  className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 hover:border-blue-300 transition-colors"
                  style={{ marginLeft: `${(ch.depth || 0) * 24}px` }}
                  data-testid={`structure-chapter-row-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-4 w-4 text-slate-400 mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold w-12 flex-shrink-0 ${
                          ch.depth === 0 ? 'text-slate-700 dark:text-slate-200'
                            : ch.depth === 1 ? 'text-blue-600 dark:text-blue-400'
                            : 'text-slate-400'
                        }`}>
                          {ch.depth === 0 ? `${idx + 1}.` : ch.depth === 1 ? '◦' : '·'}
                        </span>
                        <Input
                          data-testid={`structure-chapter-title-${idx}`}
                          className={`flex-1 ${ch.depth === 0 ? 'font-semibold' : ch.depth === 1 ? 'text-sm' : 'text-xs'}`}
                          value={ch.title}
                          onChange={(e) => updateField(idx, 'title', e.target.value)}
                          disabled={busy}
                        />
                        <div className="flex items-center gap-0.5">
                          <Button size="icon" variant="ghost" className="h-7 w-7"
                            title="Outdent (decrease depth)"
                            onClick={() => changeDepth(idx, -1)} disabled={(ch.depth || 0) === 0 || busy}
                            data-testid={`structure-chapter-outdent-${idx}`}>
                            <span className="text-xs font-bold">«</span>
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7"
                            title="Indent (increase depth)"
                            onClick={() => changeDepth(idx, +1)} disabled={(ch.depth || 0) >= 3 || busy}
                            data-testid={`structure-chapter-indent-${idx}`}>
                            <span className="text-xs font-bold">»</span>
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => move(idx, -1)} disabled={idx === 0 || busy}
                            data-testid={`structure-chapter-up-${idx}`}>
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => move(idx, 1)} disabled={idx === chapters.length - 1 || busy}
                            data-testid={`structure-chapter-down-${idx}`}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50"
                            onClick={() => remove(idx)} disabled={busy}
                            data-testid={`structure-chapter-delete-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {ch.depth === 0 && (
                        <Textarea
                          data-testid={`structure-chapter-description-${idx}`}
                          rows={2}
                          className="text-sm mb-1"
                          placeholder="1-2 sentence chapter description"
                          value={ch.description || ''}
                          onChange={(e) => updateField(idx, 'description', e.target.value)}
                          disabled={busy}
                        />
                      )}
                      {ch.rationale && ch.depth === 0 && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 italic"><strong>Rationale:</strong> {ch.rationale}</p>
                      )}
                      {(ch.subtopics || []).length > 0 && ch.depth < 2 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {ch.subtopics.slice(0, 8).map((st, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              {st}
                            </span>
                          ))}
                        </div>
                      )}
                      {(ch.suggested_evidence_keywords || []).length > 0 && (
                        <div className="mt-2 rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/30 px-2 py-1.5">
                          <div className="flex items-center gap-1 mb-1">
                            <BookOpen className="h-3 w-3 text-blue-600" />
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                              PubMed-ready keywords
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {ch.suggested_evidence_keywords.map((kw, i) => (
                              <a key={i}
                                href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(kw)}`}
                                target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs px-2 py-0.5 rounded-full bg-white dark:bg-blue-950/60 text-blue-700 dark:text-blue-200 border border-blue-300 dark:border-blue-800 hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors cursor-pointer"
                                title="Search this term on PubMed">
                                {kw} <span className="opacity-60">↗</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button size="sm" variant="ghost" className="mt-2 h-6 text-xs text-slate-500 hover:text-blue-600"
                        onClick={() => insertAfter(idx)} disabled={busy}>
                        <Plus className="h-3 w-3 mr-1" />Insert below
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox"
                  data-testid="structure-replace-existing"
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                  disabled={busy} />
                Replace existing chapters (delete all current chapters first)
              </label>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={saveAsTemplate} disabled={busy || savingTemplate || !chapters.length}
                  title="Save this structure as a reusable template"
                  data-testid="structure-save-template-btn">
                  {savingTemplate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save as template
                </Button>
                <Button variant="ghost" onClick={() => setPhase('idle')} disabled={busy} data-testid="structure-back-btn">Back</Button>
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="structure-builder-cancel-2">Cancel</Button>
                <Button onClick={handleApply} disabled={busy || !chapters.length} data-testid="structure-builder-apply-btn">
                  {phase === 'applying' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Apply {chapters.length} Chapters
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
